// Permission domains
export const PERMISSION_DOMAINS = [
  'organization', 'users', 'roles_permissions', 'jobs', 'candidates',
  'cv_processing', 'pipeline', 'interviews', 'interview_recordings',
  'communications', 'message_templates', 'analytics', 'dashboard',
  'offers', 'usage_plans', 'audit_logs', 'security_settings', 'talent_pool',
] as const;

export const PERMISSION_ACTIONS = [
  'view', 'create', 'edit', 'delete', 'assign', 'invite',
  'send', 'approve', 'reject', 'export', 'manage', 'view_sensitive', 'search',
] as const;

export const PERMISSION_SCOPES = [
  'all', 'assigned_jobs', 'department', 'assigned_candidates', 'assigned_sessions',
] as const;

export type PermissionDomain = typeof PERMISSION_DOMAINS[number];
export type PermissionAction = typeof PERMISSION_ACTIONS[number];
export type PermissionScope = typeof PERMISSION_SCOPES[number];

export interface Permission {
  domain: PermissionDomain;
  action: PermissionAction;
  scope: PermissionScope;
  source: 'role' | 'override';
}

export interface RoleDefinition {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  base_role: string;
  level: number;
  is_system: boolean;
  org_id: string | null;
  created_at: string;
}

export const DOMAIN_LABELS: Record<string, string> = {
  organization: 'Organization',
  users: 'Users',
  roles_permissions: 'Roles & Permissions',
  jobs: 'Jobs',
  candidates: 'Candidates',
  cv_processing: 'CV Processing',
  pipeline: 'Pipeline',
  interviews: 'Interviews',
  interview_recordings: 'Interview Recordings',
  communications: 'Communications',
  message_templates: 'Message Templates',
  analytics: 'Analytics',
  dashboard: 'Dashboard',
  offers: 'Offers',
  usage_plans: 'Usage & Plans',
  audit_logs: 'Audit Logs',
  security_settings: 'Security Settings',
  talent_pool: 'Talent Discovery',
};

export const ACTION_LABELS: Record<string, string> = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  assign: 'Assign',
  invite: 'Invite',
  send: 'Send',
  approve: 'Approve',
  reject: 'Reject',
  export: 'Export',
  manage: 'Manage',
  view_sensitive: 'View Sensitive',
  search: 'Search',
};

export const SCOPE_LABELS: Record<string, string> = {
  all: 'All (Org-wide)',
  assigned_jobs: 'Assigned Jobs Only',
  department: 'Department Only',
  assigned_candidates: 'Assigned Candidates',
  assigned_sessions: 'Assigned Sessions',
};

// Practical actions per domain (not every action applies to every domain)
export const DOMAIN_ACTIONS: Record<string, string[]> = {
  organization: ['view', 'edit', 'manage'],
  users: ['view', 'create', 'edit', 'delete', 'invite', 'manage', 'view_sensitive'],
  roles_permissions: ['view', 'create', 'edit', 'delete', 'manage'],
  jobs: ['view', 'create', 'edit', 'delete', 'assign'],
  candidates: ['view', 'create', 'edit', 'delete', 'export', 'view_sensitive'],
  cv_processing: ['view', 'create'],
  pipeline: ['view', 'edit', 'assign'],
  interviews: ['view', 'create', 'edit', 'assign', 'send'],
  interview_recordings: ['view', 'export'],
  communications: ['view', 'create', 'send'],
  message_templates: ['view', 'create', 'edit', 'delete'],
  analytics: ['view', 'export'],
  dashboard: ['view'],
  offers: ['view', 'create', 'send', 'approve', 'reject', 'export'],
  usage_plans: ['view', 'edit', 'manage'],
  audit_logs: ['view', 'export', 'delete'],
  security_settings: ['view', 'edit', 'manage'],
  talent_pool: ['view', 'search', 'invite', 'export', 'view_sensitive'],
};

export const ROLE_SLUG_LABELS: Record<string, string> = {
  org_owner: 'Organization Owner',
  org_admin: 'Organization Admin',
  recruiter: 'Recruiter',
  hiring_manager: 'Hiring Manager',
  interview_reviewer: 'Interview Reviewer',
  talent_coordinator: 'Talent Coordinator',
  finance_offer_approver: 'Finance / Offer Approver',
  viewer_auditor: 'Viewer / Auditor',
};
